import { gql } from "@apollo/client";
import * as y from "yup";
import ViewingArticle from "@/lib/ViewingArticle";
import { isDateRangeIn } from "./helper";
import { CONTRACTABLE_BUFFER, PURCHASABLE_BUFFER } from "@/lib/constants";

export const typeDef = gql`
  type RecommendArticle @cacheControl(scope: PRIVATE, maxAge: 0) {
    courses: [Course]
    products: [Product]
  }

  type Query {
    recommendArticle: RecommendArticle
  }

  type Mutation {
    updateRecommendArticle(type: String!, id: [Int]): RecommendArticle
    clearRecommendArticle: RecommendArticle
  }
`;

/**
 * contractAllowed.
 *
 * コースが販売期間ないか判定
 *
 * @param {Course} course
 */
function contractAllowed(course) {
  if (!course) return false;
  return isDateRangeIn(
    course.contractable_start_at,
    course.contractable_end_at,
    { buffer: CONTRACTABLE_BUFFER, default: true }
  );
}

/**
 * purchaseAllowed.
 *
 * Productが販売期間ないか判定
 *
 * @param {Product} product
 */
function purchaseAllowed(product) {
  if (!product) return false;
  return isDateRangeIn(product?.sales_start_at, product?.sales_end_at, {
    buffer: PURCHASABLE_BUFFER,
    default: true,
  });
}

/**
 * normalization.
 *
 * データの正規化
 *
 * @param {Object} recommend
 * @param {string} recommend.type - svod or tvod
 * @param {number[]} recommend.id
 * @param {Object} dataSources
 */
async function normalization(recommend, dataSources) {
  const response = {
    courses: null,
    products: null,
  };
  response.courses = await (async () => {
    if (recommend?.type !== "svod") return null;
    const courseIds = Array.isArray(recommend.id)
      ? recommend.id
      : [recommend.id];
    const promises = courseIds.map(async (id) => {
      const course = await dataSources.courses.courseById(id);
      if (contractAllowed(course)) {
        return course;
      } else {
        return null;
      }
    });
    return (await Promise.all(promises)).filter((c) => c);
  })();

  response.products = await (async () => {
    if (recommend?.type !== "tvod") return null;
    const productIds = Array.isArray(recommend.id)
      ? recommend.id
      : [recommend.id];
    const promises = productIds.map(async (id) => {
      const product = await dataSources.product.productById({
        id: id,
      });
      if (purchaseAllowed(product)) {
        return product;
      } else {
        return null;
      }
    });
    return (await Promise.all(promises)).filter((p) => p);
  })();
  return response;
}

const viewingArticleSchema = y.object({
  type: y.string().oneOf(["tvod", "svod"]),
  id: y.array(y.number()),
});

export const resolvers = {
  Query: {
    /**
     * recommendArticle
     *
     * Cookieに保存されたデータからレコメンドする商品を返す
     */
    recommendArticle: async (_parent, _args, { req, res, dataSources }) => {
      const viewingArticle = new ViewingArticle(req, res);
      const response = {
        courses: null,
        products: null,
      };

      try {
        const recommend = viewingArticle.get();
        return await normalization(recommend, dataSources);
      } catch (e) {
        console.log("recommend article Error", e);
        return response;
      }
    },
  },
  Mutation: {
    /**
     * updateRecommendArticle
     *
     * レコメンドする商品を保存する
     */
    updateRecommendArticle: async (
      _parent,
      { type, id },
      { req, res, dataSources }
    ) => {
      try {
        const recommend = await viewingArticleSchema.validate(
          { type, id },
          { stripUnknown: true }
        );
        const viewingArticle = new ViewingArticle(req, res);
        viewingArticle.set(recommend);

        return await normalization(recommend, dataSources);
      } catch (e) {
        console.log(e);
        return null;
      }
    },
    /**
     * clearRecommendArticle
     *
     * レコメンドする商品をクリアする
     */
    clearRecommendArticle: async (_parent, _args, { req, res }) => {
      const viewingArticle = new ViewingArticle(req, res);
      viewingArticle.delete();
      return {
        courses: null,
        products: null,
      };
    },
  },
};
